/**
 * CadastroRapidoProdutoDialog — formulário compacto para cadastrar produto sem sair do fluxo.
 *
 * Usado em:
 *  - Propostas (Propostas.jsx)
 *  - Sub-itens de propostas compostas (SubItensCatalogo.jsx)
 *  - Compras (Compras.jsx)
 *
 * Comportamento:
 *  - "Código / SKU" digitado vai como `sku` no payload (preserva regra de auto-geração no backend quando vazio).
 *  - Preço de Custo / Margem / Preço de Venda com cálculo recíproco automático.
 *  - Marca produto como `cadastro_incompleto: true` para completar depois.
 *  - Categorias e marcas podem vir como props ou são carregadas internamente.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus, Package } from 'lucide-react';
import api from '../lib/axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const emptyForm = {
  nome: '', codigo: '', modelo: '', categoria: '', marca: '', tipo: '',
  unidade: 'UN', procedencia: 'nacional', moeda: 'BRL',
  preco_custo: 0, margem: 0, preco_venda: 0,
};

const parseDecimal = (txt) => {
  const num = parseFloat(String(txt || '').replace(/[^\d.,-]/g, '').replace(',', '.'));
  return Number.isFinite(num) && num >= 0 ? num : 0;
};

const formatDecimal = (val) => (val === 0 || val === '' ? '' : String(val).replace('.', ','));

const CadastroRapidoProdutoDialog = ({
  open,
  onClose,
  onSuccess,
  categoriasLookup,
  marcasLookup,
  origem = 'Cadastro Rápido',
  testIdPrefix = 'quick',
}) => {
  const [form, setForm] = useState(emptyForm);
  const [isNovaCategoria, setIsNovaCategoria] = useState(false);
  const [isNovaMarca, setIsNovaMarca] = useState(false);
  const [saving, setSaving] = useState(false);
  const [internalCats, setInternalCats] = useState([]);
  const [internalMarcas, setInternalMarcas] = useState([]);

  const categorias = useMemo(
    () => (Array.isArray(categoriasLookup) ? categoriasLookup : internalCats),
    [categoriasLookup, internalCats]
  );
  const marcas = useMemo(
    () => (Array.isArray(marcasLookup) ? marcasLookup : internalMarcas),
    [marcasLookup, internalMarcas]
  );

  // Carrega lookups internamente apenas quando não vierem por prop e o dialog abrir
  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(categoriasLookup) || !Array.isArray(marcasLookup)) {
      (async () => {
        try {
          const res = await api.get('/produtos');
          const prods = res.data?.items || res.data || [];
          const cats = new Set();
          const mrs = new Set();
          prods.forEach((p) => {
            if (p.categoria) cats.add(p.categoria);
            if (p.marca) mrs.add(p.marca);
          });
          setInternalCats([...cats].sort());
          setInternalMarcas([...mrs].sort());
        } catch {
          // silencioso — lookups não são bloqueantes
        }
      })();
    }
  }, [open, categoriasLookup, marcasLookup]);

  // Reseta o form ao fechar
  useEffect(() => {
    if (!open) {
      setForm(emptyForm);
      setIsNovaCategoria(false);
      setIsNovaMarca(false);
    }
  }, [open]);

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const moedaSymbol = { BRL: 'R$', USD: 'US$', EUR: '€' }[form.moeda || 'BRL'] || 'R$';

  const onChangeCusto = (e) => {
    const custo = parseDecimal(e.target.value);
    const margem = Number(form.margem) || 0;
    // Só calcula o preço de venda automaticamente quando há margem definida (> 0).
    // Sem margem, custo e venda são independentes (não copia o custo na venda).
    setForm((f) => (margem > 0
      ? { ...f, preco_custo: custo, preco_venda: +(custo * (1 + margem / 100)).toFixed(2) }
      : { ...f, preco_custo: custo }));
  };

  const onChangeMargem = (e) => {
    const margem = parseDecimal(e.target.value);
    const custo = Number(form.preco_custo) || 0;
    const venda = +(custo * (1 + margem / 100)).toFixed(2);
    setForm((f) => ({ ...f, margem, preco_venda: venda }));
  };

  const onChangeVenda = (e) => {
    const venda = parseDecimal(e.target.value);
    const custo = Number(form.preco_custo) || 0;
    const margem = custo > 0 ? +(((venda - custo) / custo) * 100).toFixed(2) : 0;
    setForm((f) => ({ ...f, preco_venda: venda, margem }));
  };

  const handleSave = async () => {
    if (!form.nome.trim()) {
      toast.error('Informe o nome do produto');
      return;
    }
    setSaving(true);
    try {
      const { codigo, ...rest } = form;
      const payload = {
        ...rest,
        sku: (codigo || '').trim() || undefined,
        unidade: form.unidade || 'UN',
        cadastro_incompleto: true,
        observacao: `Cadastro simplificado via ${origem}`,
      };
      const response = await api.post('/produtos', payload);
      toast.success('Produto cadastrado! (Cadastro simplificado)');
      onSuccess?.(response.data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao cadastrar produto');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="cadastro-rapido-produto-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Package className="w-5 h-5" />
            Cadastro Rápido de Produto
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-3">
          <div className="p-2.5 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-700">
            <strong>Cadastro Simplificado:</strong> O produto será adicionado e poderá ter o cadastro completado posteriormente em Cadastros &gt; Produtos.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Código / SKU</Label>
              <Input
                value={form.codigo}
                onChange={(e) => updateField('codigo', e.target.value)}
                placeholder="Deixe vazio para gerar automático"
                className="h-8 text-xs"
                data-testid={`${testIdPrefix}-codigo`}
              />
            </div>
            <div>
              <Label className="text-xs">Nome do Produto *</Label>
              <Input
                value={form.nome}
                onChange={(e) => updateField('nome', e.target.value)}
                placeholder="Ex: Motor Mercury 150HP"
                className="h-8 text-xs"
                data-testid={`${testIdPrefix}-nome`}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Modelo</Label>
            <Input
              value={form.modelo}
              onChange={(e) => updateField('modelo', e.target.value)}
              placeholder="Ex: 150HP 4T"
              className="h-8 text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Categoria</Label>
              <Select
                value={isNovaCategoria ? 'nova' : form.categoria || 'selecione'}
                onValueChange={(v) => {
                  if (v === 'nova') {
                    setIsNovaCategoria(true);
                    updateField('categoria', '');
                  } else if (v === 'selecione') {
                    setIsNovaCategoria(false);
                    updateField('categoria', '');
                  } else {
                    setIsNovaCategoria(false);
                    updateField('categoria', v);
                  }
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="selecione">Selecione...</SelectItem>
                  <SelectItem value="nova">+ Nova categoria</SelectItem>
                  {categorias.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isNovaCategoria && (
                <Input
                  className="mt-1 h-7 text-xs"
                  value={form.categoria}
                  onChange={(e) => updateField('categoria', e.target.value)}
                  placeholder="Nova categoria"
                  autoFocus
                />
              )}
            </div>
            <div>
              <Label className="text-xs">Marca</Label>
              <Select
                value={isNovaMarca ? 'nova' : form.marca || 'selecione'}
                onValueChange={(v) => {
                  if (v === 'nova') {
                    setIsNovaMarca(true);
                    updateField('marca', '');
                  } else if (v === 'selecione') {
                    setIsNovaMarca(false);
                    updateField('marca', '');
                  } else {
                    setIsNovaMarca(false);
                    updateField('marca', v);
                  }
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="selecione">Selecione...</SelectItem>
                  <SelectItem value="nova">+ Nova marca</SelectItem>
                  {marcas.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isNovaMarca && (
                <Input
                  className="mt-1 h-7 text-xs"
                  value={form.marca}
                  onChange={(e) => updateField('marca', e.target.value)}
                  placeholder="Nova marca"
                  autoFocus
                />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Unidade</Label>
              <Select
                value={form.unidade || 'UN'}
                onValueChange={(v) => updateField('unidade', v)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['UN', 'PC', 'KG', 'M', 'L', 'CJ', 'PAR'].map((u) => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Procedência</Label>
              <div className="flex items-center gap-4 mt-1 p-1.5 border rounded bg-gray-50">
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input
                    type="radio"
                    name={`${testIdPrefix}-proc`}
                    value="nacional"
                    checked={form.procedencia === 'nacional'}
                    onChange={(e) => updateField('procedencia', e.target.value)}
                    className="w-3.5 h-3.5"
                  />
                  Nacional
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input
                    type="radio"
                    name={`${testIdPrefix}-proc`}
                    value="importado"
                    checked={form.procedencia === 'importado'}
                    onChange={(e) => updateField('procedencia', e.target.value)}
                    className="w-3.5 h-3.5"
                  />
                  Importado
                </label>
              </div>
            </div>
          </div>
          {/* Moeda + Preços */}
          <div className="pt-2 border-t">
            <div className="flex items-center gap-2 mb-2">
              <Label className="text-xs whitespace-nowrap">Moeda</Label>
              <Select value={form.moeda || 'BRL'} onValueChange={(v) => updateField('moeda', v)}>
                <SelectTrigger className="h-8 text-xs w-40" data-testid={`${testIdPrefix}-moeda`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BRL">R$ — Real (BRL)</SelectItem>
                  <SelectItem value="USD">US$ — Dólar (USD)</SelectItem>
                  <SelectItem value="EUR">€ — Euro (EUR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Preços */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Preço de Custo ({moedaSymbol})</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={formatDecimal(form.preco_custo)}
                onChange={onChangeCusto}
                placeholder="0,00"
                className="h-8 text-xs"
                data-testid={`${testIdPrefix}-preco-custo`}
              />
            </div>
            <div>
              <Label className="text-xs">Margem (%)</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={formatDecimal(form.margem)}
                onChange={onChangeMargem}
                placeholder="0"
                className="h-8 text-xs"
                data-testid={`${testIdPrefix}-margem`}
              />
            </div>
            <div>
              <Label className="text-xs">Preço de Venda ({moedaSymbol})</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={formatDecimal(form.preco_venda)}
                onChange={onChangeVenda}
                placeholder="0,00"
                className="h-8 text-xs"
                data-testid={`${testIdPrefix}-preco-venda`}
              />
            </div>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" size="sm" onClick={() => onClose?.()} disabled={saving}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            data-testid={`${testIdPrefix}-submit`}
            className="gap-1"
          >
            <Plus className="w-4 h-4" />
            {saving ? 'Cadastrando...' : 'Cadastrar e Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CadastroRapidoProdutoDialog;
